import { Link } from "react-router-dom";

function PrivacyPolicy() {
  return (
    <main className="mx-auto max-w-4xl px-4 pb-20 pt-10 sm:px-6">
      <article className="paper-card p-6 sm:p-10">
        <h1 className="text-4xl font-extrabold" style={{ color: "#ef852e" }}>
          Privacy Policy
        </h1>

        <p className="mt-3 text-sm text-muted-foreground">
          Last updated: August 25, 2026
        </p>

        <p className="mt-6 leading-7">
          Welcome to Kid&apos;s Sermon Builder. We respect your privacy and
          are committed to protecting the information you provide when using
          our application and website.
        </p>

        <p className="mt-4 leading-7">
          This Privacy Policy explains what information we collect, how we use
          it, how we protect it, and the choices available to you.
        </p>

        <h2 className="mt-10 text-2xl font-extrabold" style={{ color: "#ef852e" }}>
          1. Information We Collect
        </h2>

        <p className="mt-4 leading-7">
          When you create and use a Kid&apos;s Sermon Builder account, we may
          collect information such as:
        </p>

        <ul className="mt-3 list-disc space-y-2 pl-6 leading-7">
          <li>Your name.</li>
          <li>Your email address.</li>
          <li>Your account login information.</li>
          <li>Lessons, stories, sermon outlines, and other content you create.</li>
          <li>Preferences and settings you choose within the application.</li>
          <li>Technical information required to operate and secure the service.</li>
        </ul>

        <h2 className="mt-10 text-2xl font-extrabold" style={{ color: "#ef852e" }}>
          2. How We Use Your Information
        </h2>

        <p className="mt-4 leading-7">
          We use collected information to provide, maintain, and improve
          Kid&apos;s Sermon Builder.
        </p>

        <ul className="mt-3 list-disc space-y-2 pl-6 leading-7">
          <li>Create and manage your account.</li>
          <li>Provide access to lessons and application features.</li>
          <li>Save and retrieve lessons you create.</li>
          <li>Generate lesson content and related materials.</li>
          <li>Provide password reset and account-related emails.</li>
          <li>Process subscriptions and premium features.</li>
          <li>Maintain application security and prevent abuse.</li>
          <li>Improve the performance and reliability of the service.</li>
        </ul>

        <h2 className="mt-10 text-2xl font-extrabold" style={{ color: "#ef852e" }}>
          3. Children&apos;s Privacy
        </h2>

        <p className="mt-4 leading-7">
          Kid&apos;s Sermon Builder is designed for adults such as
          children&apos;s pastors, ministry leaders, Sunday school teachers,
          and parents. The service is not intended to collect personal
          information directly from children.
        </p>

        <p className="mt-4 leading-7">
          Users should not enter children&apos;s sensitive personal information
          into lesson content, story prompts, or other areas of the service.
        </p>

        <h2 className="mt-10 text-2xl font-extrabold" style={{ color: "#ef852e" }}>
          4. AI-Generated Content
        </h2>

        <p className="mt-4 leading-7">
          Kid&apos;s Sermon Builder may use artificial intelligence services to
          help generate lesson summaries, stories, activities, object lessons,
          illustrations, narration, or other educational content.
        </p>

        <p className="mt-4 leading-7">
          AI-generated content should be reviewed by an adult before it is used
          for teaching. Users are responsible for checking generated content
          for accuracy, appropriateness, and Scripture accuracy.
        </p>

        <h2 className="mt-10 text-2xl font-extrabold" style={{ color: "#ef852e" }}>
          5. Cookies and Similar Technologies
        </h2>

        <p className="mt-4 leading-7">
          We may use cookies, local storage, or similar technologies to keep
          you signed in, remember preferences, maintain sessions, and provide
          essential application functionality.
        </p>

        <h2 className="mt-10 text-2xl font-extrabold" style={{ color: "#ef852e" }}>
          6. Data Security
        </h2>

        <p className="mt-4 leading-7">
          We take reasonable technical and organizational measures to protect
          your information against unauthorized access, alteration, disclosure,
          or destruction.
        </p>

        <p className="mt-4 leading-7">
          However, no method of transmission or electronic storage can be
          guaranteed to be completely secure.
        </p>

        <h2 className="mt-10 text-2xl font-extrabold" style={{ color: "#ef852e" }}>
          7. Third-Party Services
        </h2>

        <p className="mt-4 leading-7">
          We may use third-party service providers to operate parts of the
          application, including hosting, database services, email delivery,
          artificial intelligence services, analytics, storage, and payment
          processing.
        </p>

        <p className="mt-4 leading-7">
          These providers may process information only as necessary to provide
          their services to us and according to their applicable policies.
        </p>

        <h2 className="mt-10 text-2xl font-extrabold" style={{ color: "#ef852e" }}>
          8. Subscriptions and Payments
        </h2>

        <p className="mt-4 leading-7">
          Kid&apos;s Sermon Builder may offer auto-renewing subscriptions and
          premium features. Payments made through the Apple App Store are
          processed by Apple and are subject to Apple&apos;s applicable terms
          and privacy policies.
        </p>

        <p className="mt-4 leading-7">
          We do not need to store your complete payment card information to
          provide App Store subscriptions.
        </p>

        <h2 className="mt-10 text-2xl font-extrabold" style={{ color: "#ef852e" }}>
          9. Data Retention Policy
        </h2>

        <p className="mt-4 leading-7">
          We retain your personal information, generated lessons, and outlines only for as long as necessary to fulfill the purposes outlined in this policy:
        </p>

        <ul className="mt-3 list-disc space-y-2 pl-6 leading-7">
          <li><strong>Active Accounts:</strong> User profile data, saved sermon plans, and preferences are retained for the active lifecycle of your account.</li>
          <li><strong>Temporary &amp; Cache Data:</strong> Intermediate AI prompts and temporary session data are routinely purged within 30 to 90 days.</li>
          <li><strong>Inactive Accounts:</strong> Accounts inactive for extended periods may be scheduled for archival or permanent removal after prior email notification.</li>
          <li><strong>Legal &amp; Compliance Records:</strong> Transaction logs and legal audit trails are retained for the period mandated by tax and corporate compliance laws.</li>
        </ul>

        <h2 className="mt-10 text-2xl font-extrabold" style={{ color: "#ef852e" }}>
          10. Data Deletion Policy &amp; User Rights
        </h2>

        <p className="mt-4 leading-7">
          You retain full ownership and control over your personal data and lesson material. In compliance with applicable privacy standards (including App Store account deletion requirements and global privacy regulations), you have the right to delete your data at any time:
        </p>

        <ul className="mt-3 list-disc space-y-2 pl-6 leading-7">
          <li><strong>In-App Deletion:</strong> You can delete individual sermons, drafts, or your entire account directly through your account settings inside the app.</li>
          <li><strong>Manual Deletion Requests:</strong> You may submit a written deletion request to our support team. We will process and confirm the permanent removal of your account and related records within 30 days.</li>
          <li><strong>Scope of Deletion:</strong> Deleting your account permanently clears your authentication records, personal profile, and associated sermon databases. Non-identifiable technical logs may remain in encrypted off-site backups until standard backup rotation cycles overwrite them.</li>
        </ul>

        <h2 className="mt-10 text-2xl font-extrabold" style={{ color: "#ef852e" }}>
          11. Changes to This Privacy Policy
        </h2>

        <p className="mt-4 leading-7">
          We may update this Privacy Policy from time to time. When changes are
          made, we will update the date shown at the top of this policy.
        </p>

        <h2 className="mt-10 text-2xl font-extrabold" style={{ color: "#ef852e" }}>
          12. Contact Us
        </h2>

        <p className="mt-4 leading-7">
          If you have questions about this Privacy Policy, wish to request account deletion, or need assistance regarding your data, please contact the Kid&apos;s Sermon Builder support team through the application settings or designated support channels.
        </p>

        <div className="mt-10 border-t pt-6">
          <Link
            to="/"
            className="font-bold underline"
            style={{ color: "#ef852e" }}
          >
            Back to Kid&apos;s Sermon Builder
          </Link>
        </div>
      </article>
    </main>
  );
}

export default PrivacyPolicy;